"use client";

import Header from "../../components/Header";
import Footer from "../../components/Footer";
import {
  FileText,
  Shield,
  AlertCircle,
  Users,
  Gavel,
  Mail,
} from "lucide-react";

export default function Terms() {
  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-purple-50 via-indigo-50 to-blue-50 py-16 sm:py-24 relative overflow-hidden pt-32 flex items-center min-h-96">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-10 -right-10 w-40 h-40 bg-purple-200 rounded-full opacity-20 animate-pulse"></div>
          <div
            className="absolute -bottom-16 -left-16 w-48 h-48 bg-indigo-200 rounded-full opacity-15 animate-bounce"
            style={{ animationDuration: "3s" }}
          ></div>
          <div className="absolute top-1/2 left-1/4 w-3 h-3 bg-purple-400 rounded-full animate-ping"></div>
          <div
            className="absolute top-1/3 right-1/3 w-2 h-2 bg-indigo-400 rounded-full animate-ping"
            style={{ animationDelay: "1s" }}
          ></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Terms of <span className="text-purple-600">Service</span>
            </h1>
            <p className="text-lg sm:text-xl lg:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed">
              Please read these terms carefully before using our services.
            </p>
          </div>
        </div>
      </section>

      {/* Terms Icons */}
      <section className="py-16 relative -mt-8 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <div className="bg-white rounded-lg shadow-lg p-6 text-center hover:shadow-xl transition-all duration-300">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Gavel className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Legal Agreement</h3>
              <p className="text-gray-600 text-sm">
                Binding terms between you and our service
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-lg p-6 text-center hover:shadow-xl transition-all duration-300">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Shield className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Your Protection</h3>
              <p className="text-gray-600 text-sm">
                Clear guidelines for safe service usage
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-lg p-6 text-center hover:shadow-xl transition-all duration-300">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Users className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Fair Usage</h3>
              <p className="text-gray-600 text-sm">
                Rights and responsibilities for all users
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Terms of Service Content */}
      <section className="py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-lg p-8 lg:p-12">
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-600 mb-8">
                <strong>Last updated:</strong>{" "}
                {new Date().toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </p>

              <div className="space-y-8">
                <section>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                    <FileText className="w-6 h-6 text-purple-600 mr-2" />
                    Acceptance of Terms
                  </h2>
                  <div className="text-gray-600 space-y-4">
                    <p>
                      By accessing and using TheEnterCountant website and
                      services, you accept and agree to be bound by the terms
                      and provision of this agreement. If you do not agree to
                      abide by the above, please do not use this service.
                    </p>
                    <p>
                      These Terms of Service (&quot;Terms&quot;) govern your use
                      of our website located at theentercountant.com (the
                      &quot;Service&quot;) operated by TheEnterCountant
                      (&quot;us&quot;, &quot;we&quot;, or &quot;our&quot;).
                    </p>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                    <Users className="w-6 h-6 text-purple-600 mr-2" />
                    Description of Service
                  </h2>
                  <div className="text-gray-600 space-y-4">
                    <p>
                      TheEnterCountant provides a platform connecting businesses
                      with certified accounting professionals and mentors. Our
                      services include:
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>
                        One-on-one mentoring sessions with accounting experts
                      </li>
                      <li>Financial planning and consultation services</li>
                      <li>Tax optimization guidance</li>
                      <li>Business growth advisory services</li>
                      <li>Educational resources and materials</li>
                    </ul>
                    <p>
                      We reserve the right to modify, suspend, or discontinue
                      any part of our service at any time without notice.
                    </p>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">
                    User Accounts and Registration
                  </h2>
                  <div className="text-gray-600 space-y-4">
                    <p>
                      To access certain features of our service, you may be
                      required to create an account. When creating an account,
                      you agree to:
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>
                        Provide accurate, current, and complete information
                      </li>
                      <li>
                        Maintain and update your information to keep it accurate
                        and current
                      </li>
                      <li>Maintain the security of your account credentials</li>
                      <li>
                        Accept responsibility for all activities that occur
                        under your account
                      </li>
                      <li>
                        Notify us immediately of any unauthorized access or use
                        of your account
                      </li>
                    </ul>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">
                    Booking and Payment Terms
                  </h2>
                  <div className="text-gray-600 space-y-4">
                    <p>
                      <strong>Booking Policy:</strong>
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>All bookings must be made through our platform</li>
                      <li>Sessions are subject to mentor availability</li>
                      <li>
                        We reserve the right to cancel or reschedule sessions if
                        necessary
                      </li>
                    </ul>
                    <p>
                      <strong>Payment Terms:</strong>
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>
                        Payment is required at the time of booking unless
                        otherwise specified
                      </li>
                      <li>
                        All fees are non-refundable except as specified in our
                        refund policy
                      </li>
                      <li>Prices are subject to change without notice</li>
                      <li>You are responsible for all applicable taxes</li>
                    </ul>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">
                    Cancellation and Refund Policy
                  </h2>
                  <div className="text-gray-600 space-y-4">
                    <p>
                      <strong>Cancellation by You:</strong>
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>
                        Cancellations made 24+ hours before the session: Full
                        refund
                      </li>
                      <li>
                        Cancellations made 2-24 hours before the session: 50%
                        refund
                      </li>
                      <li>
                        Cancellations made less than 2 hours before the session:
                        No refund
                      </li>
                    </ul>
                    <p>
                      <strong>Cancellation by Us:</strong>
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>
                        If we cancel a session, you will receive a full refund
                      </li>
                      <li>We may offer to reschedule instead of refunding</li>
                    </ul>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                    <AlertCircle className="w-6 h-6 text-purple-600 mr-2" />
                    Prohibited Uses
                  </h2>
                  <div className="text-gray-600 space-y-4">
                    <p>You may not use our service:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>
                        For any unlawful purpose or to solicit others to perform
                        unlawful acts
                      </li>
                      <li>
                        To violate any international, federal, provincial, or
                        state regulations, rules, laws, or local ordinances
                      </li>
                      <li>
                        To infringe upon or violate our intellectual property
                        rights or the intellectual property rights of others
                      </li>
                      <li>
                        To harass, abuse, insult, harm, defame, slander,
                        disparage, intimidate, or discriminate
                      </li>
                      <li>To submit false or misleading information</li>
                      <li>
                        To upload or transmit viruses or any other type of
                        malicious code
                      </li>
                      <li>
                        To spam, phish, pharm, pretext, spider, crawl, or scrape
                      </li>
                      <li>For any obscene or immoral purpose</li>
                      <li>
                        To interfere with or circumvent the security features of
                        our service
                      </li>
                    </ul>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">
                    Professional Advice Disclaimer
                  </h2>
                  <div className="text-gray-600 space-y-4">
                    <p className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
                      <strong>Important:</strong> The information and advice
                      provided through our platform is for educational and
                      informational purposes only. While our mentors are
                      certified professionals, the guidance provided should not
                      be considered as formal professional advice for your
                      specific situation.
                    </p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>
                        Always consult with a qualified professional for formal
                        advice
                      </li>
                      <li>We do not guarantee specific outcomes or results</li>
                      <li>
                        You are responsible for implementing any suggestions at
                        your own discretion
                      </li>
                      <li>
                        We are not liable for decisions made based on
                        information received through our platform
                      </li>
                    </ul>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">
                    Intellectual Property Rights
                  </h2>
                  <div className="text-gray-600 space-y-4">
                    <p>
                      The service and its original content, features, and
                      functionality are and will remain the exclusive property
                      of TheEnterCountant and its licensors. The service is
                      protected by copyright, trademark, and other laws. Our
                      trademarks and trade dress may not be used without our
                      written consent.
                    </p>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                    <Shield className="w-6 h-6 text-purple-600 mr-2" />
                    Limitation of Liability
                  </h2>
                  <div className="text-gray-600 space-y-4">
                    <p>
                      In no event shall TheEnterCountant, nor its directors,
                      employees, partners, agents, suppliers, or affiliates, be
                      liable for any indirect, incidental, special,
                      consequential, or punitive damages, including without
                      limitation, loss of profits, data, use, goodwill, or other
                      intangible losses, resulting from your use of the service.
                    </p>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">
                    Privacy Policy
                  </h2>
                  <div className="text-gray-600 space-y-4">
                    <p>
                      Your privacy is important to us. Please review our Privacy
                      Policy, which also governs your use of the service, to
                      understand our practices.
                    </p>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">
                    Termination
                  </h2>
                  <div className="text-gray-600 space-y-4">
                    <p>
                      We may terminate or suspend your account and bar access to
                      the service immediately, without prior notice or
                      liability, under our sole discretion, for any reason
                      whatsoever and without limitation, including but not
                      limited to a breach of the Terms.
                    </p>
                    <p>
                      If you wish to terminate your account, you may simply
                      discontinue using the service.
                    </p>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">
                    Governing Law
                  </h2>
                  <div className="text-gray-600 space-y-4">
                    <p>
                      These Terms shall be interpreted and governed by the laws
                      of Belgium, without regard to its conflict of law
                      provisions. Our failure to enforce any right or provision
                      of these Terms will not be considered a waiver of those
                      rights.
                    </p>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">
                    Changes to Terms
                  </h2>
                  <div className="text-gray-600 space-y-4">
                    <p>
                      We reserve the right, at our sole discretion, to modify or
                      replace these Terms at any time. If a revision is
                      material, we will provide at least 30 days&apos; notice
                      prior to any new terms taking effect.
                    </p>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                    <Mail className="w-6 h-6 text-purple-600 mr-2" />
                    Contact Information
                  </h2>
                  <div className="text-gray-600 space-y-4">
                    <p>
                      If you have any questions about these Terms of Service,
                      please contact us:
                    </p>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p>
                        <strong>Email:</strong> legal@theentercountant.com
                      </p>
                      <p>
                        <strong>Address:</strong> TheEnterCountant, Belgium
                      </p>
                      <p>
                        <strong>Phone:</strong> +32 (0) 123 456 789
                      </p>
                    </div>
                  </div>
                </section>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
